import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Column, Columns, EmptyState, FlexBox } from '@dixa/lib-shared-habla';
import produce from 'immer';
import { useCallback, useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useUnmount } from 'react-use';
import { searchApi, } from 'apis/search';
import { analytics } from 'app/utils/segment';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { conversationsOverviewActions } from '../../../../../assets/components/conversationsOverview/actions/conversationsOverview';
import { SearchFilters } from './components/filters/searchFilters';
import { SearchFiltersProvider } from './components/filters/searchFilters.context';
import { filterFieldsPerTab } from './components/filters/utils/definitions';
import { SearchInput } from './components/input/searchInput';
import { SearchResultsLoading } from './components/results/components/loading/searchResultsLoading';
import { SearchResults } from './components/results/searchResults';
import { SavedSearchButton } from './components/savedSearch/button/savedSearchButton';
import { SavedSearchFormModal } from './components/savedSearch/form/savedSearchFormModal';
import { SavedSearchListModal } from './components/savedSearch/list/savedSearchListModal';
import { SearchTabs } from './components/tabs/searchTabs';
import { searchActions } from './search.actions';
import styles from './search.view.module.scss';
import { createSearchUrl } from './utils/url/createSearchUrl';
import { useSearchInit } from './utils/useSearchInit/useSearchInit';
export const SearchView = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { isInitialized } = useSearchInit();
    const { tab: activeTab } = useAppPathParams('/search/:tab');
    const { params } = useAppSelector((state) => state.search);
    const [readSearchResultsQuery, { isLoading, isError }] = searchApi.useLazyReadAdvancedSearchResultsQuery();
    const readSearchResultsQueryResultRef = useRef();
    const readSearchResults = useCallback(() => {
        if (readSearchResultsQueryResultRef.current) {
            readSearchResultsQueryResultRef.current.abort();
        }
        readSearchResultsQueryResultRef.current = readSearchResultsQuery({
            type: activeTab,
            params,
        });
    }, [activeTab, params, readSearchResultsQuery]);
    useEffect(() => {
        if (isInitialized) {
            readSearchResults();
        }
    }, [isInitialized, readSearchResults]);
    useUnmount(() => {
        dispatch(conversationsOverviewActions.conversationsOverviewReset());
    });
    const updateSearchParams = useCallback((newParams) => {
        dispatch(searchActions.params.update(Object.assign(Object.assign({}, newParams), { page: 1 })));
    }, [dispatch]);
    const handleChangeTab = useCallback((tab) => {
        if (tab === activeTab)
            return;
        const nextParams = produce(params, (draft) => {
            draft.filters.conditions = draft.filters.conditions.filter((condition) => filterFieldsPerTab[tab].includes(condition.field));
        });
        updateSearchParams(nextParams);
        navigate(createSearchUrl(tab, nextParams));
        analytics.searchTabOpened({ tab });
    }, [activeTab, navigate, params, updateSearchParams]);
    const handleSubmitInput = useCallback((query, strict) => {
        const nextParams = { query, strict };
        if (!query) {
            nextParams.strict = false;
        }
        updateSearchParams(nextParams);
        analytics.searchFieldSubmitted({
            location: 'searchPage',
            isEmpty: !query,
            query,
        });
    }, [updateSearchParams]);
    const handleClearInput = useCallback(() => {
        updateSearchParams({ query: '', strict: false });
        if (!params.filters.conditions.length) {
            dispatch(searchActions.savedSearch.close());
        }
        analytics.searchFieldCleared({ location: 'searchPage' });
    }, [dispatch, params.filters.conditions.length, updateSearchParams]);
    const handleClearSavedSearch = useCallback(() => {
        dispatch(searchActions.savedSearch.close());
        navigate(createSearchUrl(activeTab, {}));
        analytics.savedSearchClosed();
    }, [activeTab, dispatch, navigate]);
    if (!activeTab ||
        !['conversations', 'notes', 'contacts'].includes(activeTab)) {
        return _jsx(Navigate, { to: "/search/conversations", replace: true });
    }
    const renderContent = () => {
        if (!isInitialized || isLoading) {
            return _jsx(SearchResultsLoading, {});
        }
        if (isError) {
            return (_jsx(FlexBox, Object.assign({ flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }, { children: _jsx(EmptyState, { title: "Something went wrong", description: "We're experiencing some issues, try again or reload the page. If this issue persists, please contact support.", icon: "exclamation", iconColor: "red.500", buttonProps: {
                        children: 'Try again',
                        onClick: readSearchResults,
                    } }) })));
        }
        return _jsx(SearchResults, { type: activeTab });
    };
    return (_jsx(Columns, { children: _jsxs(Column, { children: [_jsx(Column.Row, Object.assign({ className: styles.inputRow, noMinHeight: true }, { children: _jsx(SearchInput, { onSearch: handleSubmitInput, onClear: handleClearInput, onSavedSearchClear: handleClearSavedSearch }) })), _jsxs(Column.Row, Object.assign({ slottable: true, borderBottom: true, noMinHeight: true, noVerticalPadding: true, className: styles.tabsRow }, { children: [_jsx(Column.Slot, Object.assign({ placement: "start", className: styles.tabsSlot }, { children: _jsx(SearchTabs, { activeTab: activeTab, onTabChange: handleChangeTab }) })), _jsx(Column.Slot, Object.assign({ placement: "end" }, { children: _jsxs(FlexBox, Object.assign({ gap: "200" }, { children: [activeTab !== 'contacts' && (_jsx(SearchFiltersProvider, Object.assign({ tab: activeTab }, { children: _jsx(SearchFilters, {}) }))), _jsx(SavedSearchButton, Object.assign({ buttonProps: {
                                            variant: 'secondary',
                                        } }, { children: "Saved Searches" }))] })) }))] })), _jsx(Column.Row, Object.assign({ size: 1, noVerticalPadding: true, noHorizontalPadding: true }, { children: renderContent() })), _jsx(SavedSearchFormModal, {}), _jsx(SavedSearchListModal, {})] }) }));
};
